import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { createInvoiceDownloadUrl } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import type { PaymentEntry } from '@/types/domain'

function formatCurrencyAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return parsed.toLocaleDateString('de-AT')
}

function paymentStatusLabel(status: string): {
  label: string
  variant: 'default' | 'secondary' | 'destructive'
} {
  switch (status) {
    case 'paid':
      return { label: 'Bezahlt', variant: 'default' }
    case 'open':
      return { label: 'Offen', variant: 'secondary' }
    case 'void':
    case 'uncollectible':
      return { label: status, variant: 'destructive' }
    default:
      return { label: status || 'Unbekannt', variant: 'secondary' }
  }
}

function paymentKindLabel(kind: PaymentEntry['payment_kind']): string {
  switch (kind) {
    case 'subscription':
      return 'Abo'
    case 'manual_credit_topup':
      return 'Manuelle Aufbuchung'
    default:
      return 'Paketkauf'
  }
}

function paymentDocumentLabel(entry: PaymentEntry): string {
  if (entry.document_kind === 'invoice') {
    return 'Rechnung'
  }

  if (entry.document_kind === 'receipt') {
    return 'Beleg'
  }

  return 'Dokument'
}

function formatPaymentPeriod(start: string | null, end: string | null): string {
  if (!start && !end) {
    return '-'
  }

  return `${formatDateOnly(start)} - ${formatDateOnly(end)}`
}

type PaymentEntriesTableProps = {
  entries: PaymentEntry[]
  containerClassName?: string
}

export function PaymentEntriesTable({
  entries,
  containerClassName = 'min-h-0 flex-1 max-h-none',
}: PaymentEntriesTableProps) {
  const [activeDownloadEntryId, setActiveDownloadEntryId] = useState<string | null>(null)
  const latestDownloadRequestRef = useRef(0)

  const invoiceDownloadMutation = useMutation({
    mutationFn: async (entry: PaymentEntry) => {
      if (entry.document_kind !== 'invoice') {
        throw new Error('INVOICE_DOCUMENT_UNAVAILABLE')
      }

      const stripeInvoiceId = entry.stripe_invoice_id?.trim()
      if (!stripeInvoiceId) {
        throw new Error('STRIPE_INVOICE_REQUIRED')
      }

      return createInvoiceDownloadUrl(stripeInvoiceId)
    },
  })

  const handleInvoiceDownload = async (entry: PaymentEntry) => {
    const requestId = latestDownloadRequestRef.current + 1
    latestDownloadRequestRef.current = requestId

    try {
      setActiveDownloadEntryId(entry.id)
      const { url } = await invoiceDownloadMutation.mutateAsync(entry)

      if (latestDownloadRequestRef.current !== requestId) {
        return
      }

      window.location.assign(url)
    } catch (error) {
      if (latestDownloadRequestRef.current !== requestId) {
        return
      }

      toast.error(getErrorMessage(error))
    } finally {
      if (latestDownloadRequestRef.current === requestId) {
        setActiveDownloadEntryId((current) => (current === entry.id ? null : current))
      }
    }
  }

  return (
    <Table containerClassName={containerClassName}>
      <TableHeader>
        <TableRow>
          <TableHead>Paket</TableHead>
          <TableHead>Art</TableHead>
          <TableHead>Bezahlt am</TableHead>
          <TableHead>Zeitraum</TableHead>
          <TableHead>Betrag</TableHead>
          <TableHead>Dokument</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => {
          const status = paymentStatusLabel(entry.payment_status)

          return (
            <TableRow key={entry.id}>
              <TableCell>{entry.package_name}</TableCell>
              <TableCell>{paymentKindLabel(entry.payment_kind)}</TableCell>
              <TableCell>{formatDateTime(entry.paid_at ?? entry.created_at)}</TableCell>
              <TableCell>{formatPaymentPeriod(entry.period_start, entry.period_end)}</TableCell>
              <TableCell>{formatCurrencyAmount(entry.amount_paid_cents, entry.currency)}</TableCell>
              <TableCell>
                {entry.document_kind === 'invoice' ? (
                  entry.stripe_invoice_id ? (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-sm font-medium"
                      onClick={() => handleInvoiceDownload(entry)}
                      disabled={activeDownloadEntryId === entry.id}
                    >
                      {activeDownloadEntryId === entry.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      Rechnung herunterladen
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )
                ) : entry.document_url ? (
                  <a
                    href={entry.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {paymentDocumentLabel(entry)}
                  </a>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={status.variant}>{status.label}</Badge>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
